import { Component } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { DataDownloadOption } from './data-export/data-downloads';
import { DataDownloadFormat } from './data-export/data-export';

/**
 * Which file to download a report's data as: the formats its data source's provider has.
 *
 * The page makes the file -- it holds the respondents and their weights -- and tells this dialog how
 * it went, the way the refresh dialog is told who else reads a source.
 */
@Component({
  selector: 'app-data-download-modal',
  standalone: false,
  // The report's popup look -- see .ecr-modal in themes/end-client-reporting.scss.
  host: { class: 'ecr-modal' },
  template: `
    <div class="modal-header">
      <h5 class="modal-title nav-font16 font-weight-bold">Download data</h5>
      <button type="button" class="close" aria-label="Close" (click)="close()">
        <span aria-hidden="true">&times;</span>
      </button>
    </div>

    <div class="modal-body">
      <p class="grey5 nav-font14">
        Every respondent in {{ sourceName || "this report's data" }}, whatever the report is filtered to.
        {{ weighted
          ? "Each one carries the report's weight in a weight column."
          : "The report isn't weighted, so there's no weight column." }}
      </p>

      @for (option of options; track option.format; let last = $last) {
        <div class="choice p-3" [class.mb-2]="!last" [class.is-chosen]="chosen === option.format">
          <input type="radio" [id]="'data-download-' + option.format" name="data-download"
                 [checked]="chosen === option.format" [disabled]="preparing"
                 (change)="chosen = option.format">
          <label class="mb-0 ml-2" [for]="'data-download-' + option.format">
            <span class="nav-font14 font-weight-bold">
              <i class="fas grey4 mr-1" [class]="option.icon" aria-hidden="true"></i>{{ option.label }}
            </span>
            <span class="d-block grey4 nav-font12">{{ option.detail }}</span>
          </label>
        </div>
      }

      @if (failed) {
        <p class="grey4 nav-font12 mt-3 mb-0">
          <i class="fas fa-triangle-exclamation mr-1" aria-hidden="true"></i>The file couldn't be
          prepared. Try again.
        </p>
      }
    </div>

    <div class="modal-footer">
      <!-- Not held while preparing: closing is how someone gives up on a file that is taking too long. -->
      <button type="button" class="btn btn-sm btn-link grey5" (click)="close()">Cancel</button>
      <button type="button" class="btn btn-sm btn-primary"
              [disabled]="preparing || !chosen"
              (click)="download()">
        @if (preparing) {
          <i class="fas fa-spinner fa-spin mr-1" aria-hidden="true"></i>Preparing…
        } @else {
          <span>Download</span>
        }
      </button>
    </div>
  `,
  styles: [`
    .choice {
      display: flex;
      align-items: flex-start;
      border: 1px solid var(--nav-grey1);
      border-radius: 6px;
      cursor: pointer;
    }
    .choice.is-chosen {
      border-color: var(--nav-secondary);
      background: var(--nav-grey0, #f7f7f7);
    }
    .choice label {
      cursor: pointer;
    }
  `]
})
export class DataDownloadModalComponent {
  options: DataDownloadOption[] = [];
  sourceName = '';
  weighted = false;
  chosen?: DataDownloadFormat;
  preparing = false;
  failed = false;

  /** Set by the opener: makes and saves the chosen file, then calls downloaded() or downloadFailed(). */
  onDownload?: (option: DataDownloadOption) => void;

  constructor(private modal: BsModalRef) { }

  /** The first format starts picked, so the common case is one click. */
  offer(options: DataDownloadOption[], sourceName: string, weighted: boolean) {
    this.options = options;
    this.sourceName = sourceName;
    this.weighted = weighted;
    this.chosen = options[0]?.format;
  }

  download() {
    const option = this.options.find(candidate => candidate.format === this.chosen);
    if (!option || this.preparing) return;
    this.preparing = true;
    this.failed = false;
    this.onDownload?.(option);
  }

  downloaded() {
    this.preparing = false;
    this.close();
  }

  downloadFailed() {
    this.preparing = false;
    this.failed = true;
  }

  close() {
    this.modal.hide();
  }
}
